import { useState } from "react";
import { useSelector } from "react-redux";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { createSnackbarHelpers } from "../../shared/snackbar";

import ManufacturedItemInfo from "../../components/forms/manufacturedProducts/ManufacturedItemInfo";
import RemoveWipItemByIdForm from "../../components/forms/wips/RemoveWipItemByIdForm";
import DeleteWipOutputItemByIdForm from "../../components/forms/wips/DeleteWipOutputItemByIdForm";
import { wipLabel } from "../../shared/tenantLabels";

export function CreateWipViewModel(logout, addSnackbar) {
  const tenant = useSelector((state) => state.auth.tenant);
  const wipEntityLabel = wipLabel(tenant);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [roomOptions, setRoomOptions] = useState([]);
  const [removeReasonFromWipOptions, setRemoveReasonFromWipOptions] = useState(
    [],
  );
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [isEdit, setIsEdit] = useState(false);
  const [wip, setWip] = useState(null);

  const { showSuccess, showError } = createSnackbarHelpers(addSnackbar);

  const getRoomOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`roomsForDropdown`);

    if (callResult.success) {
      setRoomOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError("Could not fetch rooms");
    }
  };

  const createWip = async function (payload) {
    let callResult = await makePostApiCall("wips/create", {
      ...payload,
    });

    if (callResult.success) {
      setWip(callResult.data);
      setIsEdit(true);
      showSuccess(`${wipEntityLabel} created`);
      window.location = `/wips/${callResult.data.id}`;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(callResult.data.message);
    }
    return callResult;
  };

  const updateWip = async function (payload) {
    let callResult = await makePutApiCall(`wips/${wip.id}`, {
      ...payload,
    });

    if (callResult.success) {
      showSuccess(`${wipEntityLabel} updated`);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(callResult.data.message);
    }
    return callResult;
  };

  const onAddItemToWipByBarcode = async function (scannedBarcode) {
    let callResult = await makePostApiCall("wipItems/add", {
      wip_id: wip.id,
      barcode: scannedBarcode,
    });

    if (callResult.success) {
      setWip({
        ...wip,
        incoming_items: callResult.data.items,
        incoming_info: callResult.data.info,
      });
      showSuccess(`Item added to ${wipEntityLabel}`);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(callResult.data.message);
    }
    return callResult;
  };
  const onRemoveItemWipByBarcode = async function (payload) {
    let callResult = await makePostApiCall("wipItems/remove", {
      wip_id: wip.id,
      ...payload,
    });

    if (callResult.success) {
      setWip({
        ...wip,
        incoming_items: callResult.data.items,
        incoming_info: callResult.data.info,
      });
      showSuccess(`Item removed from ${wipEntityLabel}`);

      setTimeout(() => {
        showSuccess(`NEW BARCODE: ${callResult.data.newBarcode}`);
      }, 500);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(callResult.data.message);
    }
    return callResult;
  };

  const getRemoveReasonsOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`reasonCodesForDropdown`);

    if (callResult.success) {
      setRemoveReasonFromWipOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError("Could not fetch reasons to remove");
    }
  };

  const onGetWipItemByBarcode = async function (barcode) {
    let callResult = await makeGetApiCall(`wipItems/barcode/${barcode}`);

    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError("Could not fetch barcode information");
    }
    return callResult;
  };

  const openSlideForManufacturedItemDetails = async function (manProdId) {
    let callResult = await makeGetApiCall(`manufacturedProducts/${manProdId}`);

    if (callResult.success) {
      setSlideOver({
        childComponent: (
          <ManufacturedItemInfo item={callResult.data} setOpen={setSlideOver} />
        ),
        open: true,
      });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError("Could not fetch manufactured item");
    }
  };

  const onAskReturnIncomingItem = (wipItem) => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <RemoveWipItemByIdForm
          onRemoveItemWipByBarcode={onRemoveItemWipByBarcode}
          removeReasonFromWipOptions={removeReasonFromWipOptions}
          setEmptyModelOpen={setEmptyModelOpen}
          item={wipItem}
        />
      ),
    });
  };

  const onDeleteWipOutputItemByBarcode = async function (
    deletedProduct,
    reason,
  ) {
    let callResult = await makePostApiCall(
      `manufactureProducts/deleteByBarcode/${deletedProduct.barcode}`,
      {
        reason_id: reason.value,
        reason_name: reason.label,
        wip_id: wip.id,
      },
    );

    if (callResult.success) {
      setWip({
        ...wip,
        outgoing_items: callResult.data.items,
        outgoing_info: callResult.data.info,
      });
      showSuccess(`Item deleted from outgoing ${wipEntityLabel}`);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(callResult.data.message);
    }
    return callResult;
  };

  const onAskDeleteWipOutputItem = (wipItem) => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <DeleteWipOutputItemByIdForm
          onDeleteWipOutputItemByBarcode={(reason) =>
            onDeleteWipOutputItemByBarcode(wipItem, reason)
          }
          removeReasonFromWipOptions={removeReasonFromWipOptions}
          setEmptyModelOpen={setEmptyModelOpen}
          item={wipItem}
        />
      ),
    });
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    roomOptions,
    getRoomOptionsForDropdown,
    isEdit,
    createWip,
    wip,
    onAddItemToWipByBarcode,
    onRemoveItemWipByBarcode,
    removeReasonFromWipOptions,
    getRemoveReasonsOptionsForDropdown,
    onGetWipItemByBarcode,
    updateWip,
    setSlideOver,
    slideOver,
    openSlideForManufacturedItemDetails,
    onAskReturnIncomingItem,
    onAskDeleteWipOutputItem,
    showError,
  };
}
